import type { IDocuments } from 'views/signed-doc-inbox';

import { createRef, FC, useCallback, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import {
	Button,
	Image,
	Loader,
	Input,
	ReactResponsiveTable,
} from '@storybook';
import Tippy from '@tippyjs/react';
import {
	AllConditionalPipelinesState,
	AllPipelinesState,
	FundsState,
	SelectedDocFilesForUploadState,
	SelectedDocsForUploadState,
	SelectedPipelineDetails,
	UploadedDocsState,
	useSignedDocs,
} from 'global-stores';
import { useAllowedInvites, useNetwork, useNotification, useOutsideClick, useTimezoneDate } from 'hooks';
import { formatSizeUnits } from 'utils';
import { WelcomeNewUserModalState } from 'views/new-user-popup';
import {
	ComplexConfigurationInvite,
	IComplexConfig,
	IFundInvestment,
	ISignAgreement,
	IsQrInviteState,
} from 'views/pipelines/components/store';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';
import { PipelineInstanseTable } from '../../constants';
import {
	IAgreements,
	IOnboardingQRModalNavigate,
	ISelectedPipelineQrState,
	OnboardingQRModalNavigateState,
	OnboardingQRModalOpenState,
	SelectedPipelineDetailsForQRState,
	SelectedPipelineQrData,
	SelectedPipelineQrListState,
	SelectedQRInstanceState,
	SelectedQrInstanceState,
	SelectedQrOnbordingFlowName,
	SelectedQRSate,
	SelectedQRTemplateIdState,
	useOnboardingQrFlow,
} from '../../stores';
import { DropOption } from '../dropOptions';
import { ErrorSessionsNotWrite } from '../../../../constants';

import './onboarding-qr-table.scss';
// import { envHost } from 'helpers';

import { API_URL } from 'constant';
import { usePipelineData } from 'views';
interface IOnboardingQRTable {
	isLoadingQrList: boolean;
	setQrLoadingState?: () => void;
	piplineData?: any;
	selectedPipelineQr?: any;
	combinedOnboarding?: any;
	/* RENDER THE sv2 SUB-TABLE INSTEAD OF ReactResponsiveTable.
	 *
	 * Opt-in, and only the redesigned /onboarding deck passes it. This same
	 * component is still mounted by the legacy views/pipelines screen, which has
	 * not been through the sv2 pass — switching unconditionally would restyle a
	 * page nobody asked me to touch. The legacy branch is untouched below, and
	 * onboarding-qr-table.scss stays for it. */
	sv2?: boolean;
}

export const OnboardingQRTable: FC<IOnboardingQRTable> = ({
	isLoadingQrList,
	piplineData,
	selectedPipelineQr,
	combinedOnboarding,
	sv2 = false,
}) => {
	// globle states
	const [pipelineDetails, setPipelineDetailsForQR] = useRecoilState(
		SelectedPipelineDetailsForQRState
	);
	const selectedPipelineQrList = useRecoilValue(SelectedPipelineQrListState);
	const setNavigate = useSetRecoilState(OnboardingQRModalNavigateState);
	const setQRTemplateId = useSetRecoilState(SelectedQRTemplateIdState);
	const setQRInstanceState = useSetRecoilState(SelectedQRInstanceState);
	const setQrInstance = useSetRecoilState(SelectedQrInstanceState);
	const setQrOnbordingFlowName = useSetRecoilState(SelectedQrOnbordingFlowName);
	const setIsOnboaringQrModal = useSetRecoilState(OnboardingQRModalOpenState);
	const [, setSelectedQRSate] = useRecoilState(SelectedQRSate);
	const setQRflowData = useSetRecoilState(SelectedPipelineQrData);
	const setPipelineDetails = useSetRecoilState(SelectedPipelineDetails);

	const conditionalPipelines = useRecoilValue(AllConditionalPipelinesState);
	const pipelineData = useRecoilValue(AllPipelinesState);
	const setWelcomeNewUser = useSetRecoilState(WelcomeNewUserModalState);
	const funds = useRecoilValue(FundsState);
	// Pradeep chaurasia : state for selected files
	const setSelectedFiles = useSetRecoilState(SelectedDocsForUploadState);
	const setSelectedDocFiles = useSetRecoilState(SelectedDocFilesForUploadState);

	const setIsQrFlow = useSetRecoilState(IsQrInviteState);

	const setAddedFiles = useSetRecoilState(UploadedDocsState);
	const setConfiguration = useSetRecoilState(ComplexConfigurationInvite);

	//local state
	const [showPopup, setShowPopup] = useState({
		id: '',
	});
	const [editingInstanceId, setEditingInstanceId] = useState<string | null>(null);
	const [editingInstanceName, setEditingInstanceName] = useState<string>('');
	const [isEditingApiLoader, setIsEditingApiLoader] = useState(false);

	// hooks
	const { deleteQrInstance } = useOnboardingQrFlow();
	const { isAllowedToInvite } = useAllowedInvites();
	const { checkUserWritePermission } = useUserRoles();
	const { getDocument } = useSignedDocs();
	const { patch } = useNetwork();
	const { successNotification, errorNotification } = useNotification();
	const { getPinelines, pipelinesLoading } = usePipelineData();
	const { formatDate } = useTimezoneDate();

	const isUserPermissionWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Onboarding),
		[checkUserWritePermission]
	);
	const isSessionWritePermission = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Sessions),
		[checkUserWritePermission]
	);

	//ref
	const ref = createRef<HTMLDivElement>();
	useOutsideClick(ref, () => {
		setShowPopup({ id: '' });
	});

	const selectedPipelineQRs = useMemo(
		() => (selectedPipelineQr ? selectedPipelineQr : selectedPipelineQrList),
		[selectedPipelineQr, selectedPipelineQrList]
	);
	const selectedPipelines = useMemo(
		() => (piplineData ? piplineData : pipelineDetails),
		[pipelineDetails, piplineData]
	);

	const getFundName = useCallback(
		(fundId: string) => {
			return (
				funds?.find(({ id, _id }) => id === fundId || _id === fundId)?.name ||
				'--'
			);
		},
		[funds]
	);

	// table columns
	const ColumnHeaders = useMemo(() => {
		const arr: any[] = [];
		if (PipelineInstanseTable?.HEADER) {
			PipelineInstanseTable?.HEADER.forEach(item => {
				if (
					item.key === 'payIn' &&
					!selectedPipelines?.fullStatusData?.includes('payIn')
				) {
					return;
				}
				if (
					item.key === 'signAgreement' &&
					!selectedPipelines?.fullStatusData?.includes('signAgreement')
				) {
					return;
				}
				if (item.key === '506b' && !selectedPipelineQRs?.[0]?.accreditation) {
					return;
				}
				arr.push(item);
			});
		}
		if (!isUserPermissionWrite) {
			return (arr ?? []).filter(headerItem => headerItem.key !== 'action');
		}
		return arr;
	}, [selectedPipelines, selectedPipelineQRs, isUserPermissionWrite]);

	const circledDocs = useCallback(
		(key: string) => (
			<div key={key} className="doc-wrapper">
				<Image fileName="PDF.svg" />
			</div>
		),
		[]
	);

	const renderSignDocs = useCallback(
		(item: ISelectedPipelineQrState, key: 'agreements') => (
			<>
				{item[key].map(
					({ _id }, index: number) => index < 3 && circledDocs(_id)
				)}
				{item[key]?.length > 3 && (
					<div
						onMouseLeave={() =>
							setShowPopup({
								id: '',
							})
						}
						onMouseEnter={() =>
							setShowPopup({
								id: !showPopup.id
									? item._id
									: showPopup.id === item._id
									? ''
									: item._id,
							})
						}
						className={`doc-wrapper docs-wrapper__see-more ${
							item._id === showPopup.id
								? 'doc-wrapper docs-wrapper__see-more--clicked'
								: ''
						}`}
					>
						+{item[key]?.length - 3}
						{item._id === showPopup.id && (
							<div
								ref={ref}
								onClick={e => e.stopPropagation()}
								className="docs-wrapper__see-more-popup"
							>
								{item[key].map(
									({ _id }, index: number) => index >= 3 && circledDocs(_id)
								)}
							</div>
						)}
					</div>
				)}
			</>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[ref, showPopup.id]
	);

	const handleClickOnActions = useCallback(
		(
			navigateKey: IOnboardingQRModalNavigate,
			item: ISelectedPipelineQrState
		) => {
			setQrOnbordingFlowName(piplineData?.name ?? '');
			setQRflowData(item);
			setIsOnboaringQrModal(true);
			setQrInstance(item);
			setNavigate(navigateKey);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const handleInviteCSV = useCallback(
		(item: any) => {
			if (!selectedPipelines.isInviteAllowed || !isSessionWritePermission) {
				return;
			}
			if (!isAllowedToInvite('onboarding')) {
				setWelcomeNewUser({
					open: true,
					type: 'Credits_Remain',
					serviceName: 'Onboarding Service',
				});
				return;
			}
			const overlayData = item?.agreements?.find(
				(item: IAgreements) => item.templateType == 'overlay'
			);
			const filterData = (
				overlayData ? conditionalPipelines : pipelineData
			)?.find(subItem => subItem?._id === item?.pipelineId);
			setPipelineDetails(filterData);
			setPipelineDetailsForQR(filterData);
			setQRflowData(item);
			setSelectedQRSate(item._id);
			setNavigate('csv');
			if (overlayData) {
				setQRInstanceState(item?.agreements);
				setQRTemplateId(overlayData.templateId);
			}
			setIsOnboaringQrModal(true);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			isAllowedToInvite,
			pipelineData,
			conditionalPipelines,
			selectedPipelines.isInviteAllowed,
		]
	);

	const selectedArchiveQR = useCallback(
		(piplelineId: string) => {
			const selectedFlow = combinedOnboarding.filter(
				(item: any) => item?._id === piplelineId
			);
			setPipelineDetails(selectedFlow?.[0]);
			return selectedFlow?.[0];
		},
		[combinedOnboarding, setPipelineDetails]
	);

	/**
	 * Handles the reconfiguration of a QR code for onboarding.
	 * @param {Event} e - The event that triggered the reconfiguration.
	 * @param {Object} item - The item containing banks, agreements, and pipelineId for reconfiguration.
	 */
	const handleReConfiguredQR = useCallback(
		(e: any, item: IComplexConfig) => {
			e.stopPropagation();

			// Extract data from the item object
			const {
				banks = [],
				agreements = [],
				pipelineId = '',
				_id: qrID = '',
			} = item ?? {};
			setSelectedQRSate(qrID);
			// Find and set pipeline details for the QR
			const combinedOnboardingData = (conditionalPipelines ?? []).concat(
				pipelineData ?? []
			);
			const pipelineDetails = combinedOnboardingData.find(
				el => el._id === pipelineId
			);
			setPipelineDetailsForQR(pipelineDetails ?? {});
			// Select the archive QR and set navigation and modal states
			selectedArchiveQR(pipelineId);
			setNavigate('reconfigQR');
			setIsOnboaringQrModal(true);
			setIsQrFlow(true);
			// Transform banks into fundInvestment
			const fundInvestment = (banks ?? []).map((bank: IFundInvestment) => ({
				...bank,
				node: bank.nodeId,
			}));
			// Update configuration state with signAgreement and fundInvestment data
			setConfiguration({
				signAgreement: agreements,
				fundInvestment,
			});
			// Process each agreement and related documents
			(agreements ?? []).forEach(async (agreement: ISignAgreement) => {
				const documentData = await getDocument(agreement.templateId);
				const {
					name: templateName,
					documents = [],
					docRefId,
					createdAt,
					prepareUrl,
				} = documentData || {};
				let eSignTempsize = 0;

				documents.forEach((doc: IDocuments) => {
					eSignTempsize += Number(doc.document.size);
				});
				const name: string = documents[0]?.document?.name;

				// Create a payload object for the agreement
				const payload = {
					...agreement,
					name,
					templateName,
					createdAt,
					prepareUrl,
					configured: true,
					isChecked: true,
					size:
						formatSizeUnits(
							parseFloat(docRefId?.size ?? eSignTempsize ?? 0)
						).toString() ?? '-',
				};

				// Update addedFiles and selectedFiles arrays with the payload
				setAddedFiles(prev => [...prev, payload]);
				setSelectedFiles(prev => [...prev, payload]);
				setSelectedDocFiles(prev => [...prev, payload])
			});
		},
		[
			conditionalPipelines,
			pipelineData,
			selectedArchiveQR,
			setAddedFiles,
			setConfiguration,
			setIsOnboaringQrModal,
			setNavigate,
			setPipelineDetailsForQR,
			setSelectedFiles,
			setSelectedQRSate,
			setIsQrFlow,
			getDocument,
			setSelectedDocFiles,
		]
	);

	const handleDescription = useMemo(
		() => (
			<span className="actions-wrapper__description-light">
				Are you sure you want to archive this QR code instance?
			</span>
		),
		[]
	);

	const handleEditInstanceName = (item: any) => {
		if (!isUserPermissionWrite) return;
		setEditingInstanceId(item._id);
		setEditingInstanceName(item.name || '');
	};

const handleInstanceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingInstanceName(e.target.value);
};

const handleSaveInstanceName = useCallback(async (item: any) => {
    if (!isUserPermissionWrite) return;
    setIsEditingApiLoader(true);
    try {
        const res = await patch(`${API_URL.COMPLEX_QR}/${item._id}`, {
            name: editingInstanceName,
            pipelineId: item.pipelineId,
        });
        if (res && res.success) {
            successNotification('QR instance name updated successfully');
			await getPinelines();
        } else {
            errorNotification(res?.message || 'Failed to update QR instance name');
        }
        setEditingInstanceId(null);
    } catch (error: any) {
        errorNotification(error?.message || 'Network error while updating QR instance name');
        setEditingInstanceId(null);
    } finally {
        setIsEditingApiLoader(false);
    }
},[isUserPermissionWrite, editingInstanceName, errorNotification, getPinelines, patch, successNotification]);
	// table rows
	const tableRow = useMemo(() => {
		const rows: any[] = [];
		if (selectedPipelineQRs) {
			selectedPipelineQRs.forEach((item: any) => {
				/* DECLARED PER INSTANCE, NOT ONCE FOR THE WHOLE LIST.
				 *
				 * This was `let row = {}` outside the loop, with every iteration
				 * spreading the PREVIOUS row and pushing the result — so each row
				 * inherited the one before it. Harmless only because every branch
				 * below happens to set its key on every pass; the moment one does
				 * not, that cell silently shows the previous instance's value,
				 * which is the kind of wrong that reads as correct. */
				let row: any = {};
				const isArchived = item?.isDeleted;
				/* Identity and state the sv2 renderer needs for keys and the
				   archived styling. Prefixed so they cannot collide with a column
				   key coming from PipelineInstanseTable.HEADER. */
				row._id = item?._id;
				row._archived = !!isArchived;

				const { agreements } = item ?? {};
				/*
				 * ABSENT MEANS BASIC, NOT OVERLAY.
				 *
				 * This gate hides the QR and Embed actions, and it exists for a
				 * real reason: an OVERLAY agreement has to be configured per
				 * recipient before an invite, so an anonymous QR scan or an iframe
				 * embed cannot produce a valid session for one. Only overlay
				 * should suppress them.
				 *
				 * Testing `=== 'basic'` made a MISSING value suppress them too,
				 * and missing is common: `templateType` is a mongoose default, so
				 * anything that does not hydrate through the model never has it —
				 * the deck's instance list is served by a raw `$lookup`
				 * aggregation (complex-flow/index.ts), and any record written
				 * outside the model lacks the field entirely. Both actions then
				 * silently vanished with nothing to explain it.
				 *
				 * Identical behaviour for well-formed data, where the field is
				 * `'basic' | 'overlay'`; the difference is only in how an absent
				 * value is read, and the schema default says it is basic.
				 */
				const isBasicFlow = (agreements ?? []).every(
					(agreement: any) => agreement?.templateType !== 'overlay'
				);

				// TODO: added complex type
				const isComplex = !!item?.banks;
				(ColumnHeaders ?? []).forEach(({ key, format }) => {
					if (format === 'jsx' && key === '506b') {
						const value = () => (
							<>
								{item?.['accreditation']
									? circledDocs(item['accreditation'])
									: '--'}
							</>
						);

						row = { ...row, [key]: value };
						return;
					}
					if (format === 'jsx' && key === 'signAgreement') {
						const value = () => (
							<>
								<div className="docs-wrapper">
									{item?.agreements && item.agreements.length
										? renderSignDocs(item, 'agreements')
										: '--'}
								</div>
							</>
						);

						row = { ...row, [key]: value };
						return;
					}
					if (format === 'jsx' && key === 'date') {
						const value = () => (
							sv2 ? (
								/* Stacked, as the design has it: the DATE is what a reader
								   scans for and the time is qualifying detail, so they are not
								   competing on one line. */
								<div className="qr-dt">
									<span className="d">
										{item?.['createdAt']
											? formatDate(item?.['createdAt'], 'MMM dd, yyyy', true)
											: '--'}
									</span>
									{item?.['createdAt'] && (
										<span className="t">
											{formatDate(item?.['createdAt'], 'hh:mm a')}
										</span>
									)}
								</div>
							) : (
							<div className="created-date">
								{item?.['createdAt'] ? formatDate(item?.['createdAt'], 'MMM dd, yyyy', true) : '--'}{' '}
								{item?.['createdAt'] ? (
									<span className="subId">{formatDate(item?.['createdAt'], 'hh:mm a')}</span>
								) : (
									''
								)}
							</div>
							)
						);

						row = { ...row, [key]: value };
						return;
					}
					if (format === 'jsx' && key === 'action') {
						const value = () =>
							!isUserPermissionWrite ? (
								<></>
							) : (
								<div className={sv2 ? 'qr-act' : 'actions-wrapper'}>
									{!item.isDeleted ? (
										<>
											<Tippy
												content={
													!isSessionWritePermission ? (
														<div style={{ textAlign: 'center' }}>
															{ErrorSessionsNotWrite}
														</div>
													) : (
														''
													)
												}
												disabled={isSessionWritePermission}
											>
												<>
													<div
														className={
															sv2
																? `reinv ${
																		!isSessionWritePermission ? 'disabled-btn' : ''
																  }`
																: `invite_column ${
																		selectedPipelines?.className ?? ''
																  } ${!isSessionWritePermission && 'disabled-btn'}`
														}
														onClick={() => handleInviteCSV(item)}
													>
														Invite
													</div>
												</>
											</Tippy>

											{!isArchived && isBasicFlow ? (
												<Tippy content={isArchived ? 'Archived item' : 'QR'}>
													{sv2 ? (
														<button
															type="button"
															className="qr-ib"
															aria-label="QR"
															onClick={() =>
																handleClickOnActions('qr_image', item)
															}
														>
															<i className="ri-qr-code-line" />
														</button>
													) : (
														<div className="actions-wrapper-btn">
															<Button
																label={
																	<i className="ri-qr-code-line actions-wrapper-btn-config" />
																}
																handleClick={() =>
																	handleClickOnActions('qr_image', item)
																}
																type="menu-item actions-wrapper__hover"
															/>
														</div>
													)}
												</Tippy>
											) : (
												/* NO GHOST SPACER IN sv2. The legacy layout reserved
												   34px so the columns lined up whether or not the icon
												   rendered; in the sv2 row that reads as a hole between
												   Invite and the kebab. The actions sit together and
												   the column width does the aligning. */
												!sv2 && <div style={{ width: 34 }} />
											)}

											{sv2 ? (
												!isArchived &&
												isBasicFlow && (
													<Tippy content="Embed">
														<button
															type="button"
															className="qr-ib"
															aria-label="Embed"
															onClick={() =>
																handleClickOnActions('embed_code', item)
															}
														>
															<i className="ri-code-s-slash-line" />
														</button>
													</Tippy>
												)
											) : (
											<Tippy content={isArchived ? 'Archived item' : 'Embed'}>
												<div className="actions-wrapper-btn">
													{!isArchived && isBasicFlow ? (
														<Button
															label={
																<i className="ri-code-s-slash-line actions-wrapper-btn-config" />
															}
															handleClick={() =>
																handleClickOnActions('embed_code', item)
															}
															type="menu-item actions-wrapper__hover"
														/>
													) : (
														<div style={{ width: 34 }} />
													)}
												</div>
											</Tippy>
											)}
											<DropOption
												description={handleDescription}
												handleDelete={() =>
													deleteQrInstance(item?._id, isComplex)
												}
											/>
										</>
									) : (
										piplineData.type === 'complex' && (
											<div
												className="invite_column"
												onClick={e => handleReConfiguredQR(e, item)}
											>
												Re-configure
											</div>
										)
									)}
								</div>
							);

						row = { ...row, [key]: value };
						return;
					}
					if (key === 'instanceName') {
						row = {
							...row,
							[key]: (
								<div className={sv2 ? 'qr-nm' : 'instance-name-container'}>
									{editingInstanceId === item._id ? (
										sv2 ? (
											/* A PLAIN INPUT, SIZED TO THE CELL.
											 *
											 * The storybook <Input> is a form control — 48px tall with
											 * its own label slot and full-width box — so dropping it
											 * into a table row made the rename box swallow most of the
											 * row and pushed the row height from 40 to 75px. Renaming
											 * in place wants a field the width of the name it is
											 * replacing, with the confirm/cancel beside it. */
											<div className="qr-ren">
												<input
													type="text"
													autoFocus
													value={editingInstanceName}
													onChange={handleInstanceNameChange}
													onKeyDown={e => {
														if (e.key === 'Enter') handleSaveInstanceName(item);
														if (e.key === 'Escape') setEditingInstanceId(null);
													}}
													aria-label="Instance name"
												/>
												{isEditingApiLoader || pipelinesLoading ? (
													<Loader dimension={16} type="loader" />
												) : (
													<>
														<button
															type="button"
															className="qr-ib ok"
															aria-label="Save"
															onClick={() => handleSaveInstanceName(item)}
														>
															<i className="ri-check-line" />
														</button>
														<button
															type="button"
															className="qr-ib no"
															aria-label="Cancel"
															onClick={() => setEditingInstanceId(null)}
														>
															<i className="ri-close-line" />
														</button>
													</>
												)}
											</div>
										) : (
										<>
											<Input
												inputType="text"
												placeholder=""
												label={''}
												value={editingInstanceName}
												autoFocus
												handleChange={handleInstanceNameChange}
												className="instance-name-input"
											/>
											{isEditingApiLoader || pipelinesLoading ? (
												<Loader dimension={20} type="loader" />
											) : (
												<>
													<i
														className="ri-check-line actions-wrapper-btn-config instance-name-check-icon"
														onClick={() => handleSaveInstanceName(item)}
													/>
													<i
														className="ri-close-line actions-wrapper-btn-config instance-name-cross-icon"
														onClick={() => setEditingInstanceId(null)}
													/>
												</>
											)}
										</>
										)
									) : (
										<>
											{sv2 && item?.['name']?.length <= 17 ? (
												<span className="nm">{item?.['name']}</span>
											) : item?.['name']?.length > 17 ? (
												<Tippy placement="top-start" content={item['name']}>
													<div
														className={sv2 ? 'nm' : 'text-elipsis'}
														style={sv2 ? undefined : { paddingLeft: '0px' }}
													>
														{item?.['name']}
													</div>
												</Tippy>
											) : (
												item?.['name']
											)}
											{isUserPermissionWrite && (
												<i
													className={
														sv2 ? 'ri-pencil-line pencil' : 'ri-pencil-line'
													}
													style={
														sv2 ? undefined : { cursor: 'pointer', marginLeft: 8 }
													}
													onClick={() => handleEditInstanceName(item)}
												/>
											)}
										</>
									)}
									{item?.isDeleted &&
										(sv2 ? (
											<span className="vch pur qr-arch-tag">
												New changes added
											</span>
										) : (
											<div className="archive-label">New changes added</div>
										))}
								</div>
							),
						};
					}
					if (key === 'payIn') {
						let fundValue =
							item?.fund?.name ?? getFundName(item?.fundId) ?? '--';
						if (isComplex) {
							fundValue = item?.banks.map((bank: any) => {
								return (
									<div key={bank?.accountId}>
										{bank?.fundName ?? getFundName(bank?.fundId) ?? '--'}
									</div>
								);
							});
						}
						row = { ...row, [key]: fundValue };
					}
				});
				rows.push(row);
			});
		}
		return rows.reverse();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		selectedPipelineQRs,
		ColumnHeaders,
		circledDocs,
		renderSignDocs,
		isUserPermissionWrite,
		isSessionWritePermission,
		selectedPipelines?.className,
		handleDescription,
		piplineData.type,
		handleInviteCSV,
		handleClickOnActions,
		deleteQrInstance,
		handleReConfiguredQR,
		editingInstanceId,
		editingInstanceName,
		isEditingApiLoader,
		pipelinesLoading,
		handleSaveInstanceName,
		getFundName,
		formatDate,
	]);

	/* THE sv2 SUB-TABLE.
	 *
	 * `ReactResponsiveTable` brings its own chrome — a #edf1fc header band and
	 * #f5f5f5 rows — which is why this panel rendered as a white table dropped
	 * into a dark deck. The cells themselves were already fine: `tableRow`
	 * produces React nodes, so the fix is to stop wrapping them in the legacy
	 * table and lay them out with the deck's own `.stbl` instead.
	 *
	 * Columns come from the same `ColumnHeaders` the legacy branch uses, so the
	 * conditional ones (payIn / signAgreement / 506(b)) appear and disappear
	 * exactly as before — the table has to hold up anywhere from 3 to 6 columns.
	 * The three constant ones get real widths from `.qn/.qd/.qa`; the rest share
	 * what is left, under the `table-layout: fixed` the host `.stbl` sets. */
	if (sv2) {
		if (isLoadingQrList) {
			return <div className="sv2-loading">Loading QR instances…</div>;
		}
		if (!selectedPipelineQRs?.length) {
			return (
				<div className="sv2-empty">
					No QR instance available — none has been created for this flow yet.
				</div>
			);
		}
		const cls: Record<string, string> = {
			instanceName: 'qn',
			date: 'qd',
			action: 'qa',
		};
		/* NO `.tscroll` WRAPPER HERE, DELIBERATELY.
		 *
		 * It buys nothing — the sub-table is `table-layout: fixed` inside a panel
		 * that already spans the row, so 3 to 6 columns compress rather than
		 * overflow — and it costs two visible bugs. `overflow-x: auto` makes the
		 * OTHER axis compute to `auto` as well, which is where the stray vertical
		 * scrollbar came from; and an overflow container clips descendants, so
		 * the kebab's absolutely-positioned menu opened inside the scroll box and
		 * was cut off exactly where Archive sits. */
		return (
			<>
				<table className="stbl qr-tbl">
					<thead>
						<tr>
							{(ColumnHeaders ?? []).map((h: any) => (
								<th key={h.key} className={cls[h.key] ?? ''}>
									{h.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{tableRow.map((r: any, i: number) => (
							<tr
								key={r._id ?? i}
								className={r._archived ? 'qr-archived' : undefined}
							>
								{(ColumnHeaders ?? []).map((h: any) => {
									// Cells arrive as either a node or a thunk returning one.
									const cell = r[h.key];
									return (
										<td key={h.key} className={cls[h.key] ?? ''}>
											{typeof cell === 'function' ? cell() : cell ?? '--'}
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</>
		);
	}

	return (
		<div className="onboarding-flow-instance-wrapper">
			{isLoadingQrList ? (
				<div className="onboarding-flow-instance-wrapper__loader">
					<Loader type="gif" />
				</div>
			) : selectedPipelineQRs && selectedPipelineQRs.length ? (
				<ReactResponsiveTable
					isLoading={isLoadingQrList}
					tableType="pipeline_qr_list"
					column={ColumnHeaders}
					rows={tableRow}
					emptyHeight={'100%'}
					EmptyIllustration={'empty-api-list.svg'}
					EmptyMessageHeading={'No QR Instance Available'}
					EmptyMessageDescription={'You have not created any QR instance yet.'}
					headerZIndex={0}
				/>
			) : (
				<></>
			)}
			
		</div>
	);
};
